import { useEffect, useRef, useState } from 'react';

import { SearchIcon, XIcon } from 'lucide-react';

import useDebounce from '@/components/hooks/useDebounce';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { DEBOUNCE_DELAY, MIN_SEARCH_LENGTH } from '@/constants';

export interface GlobalSearchProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const GlobalSearch = ({
  value,
  onChange,
  placeholder = 'Search...',
  className = 'w-80',
}: GlobalSearchProps) => {
  const [search, setSearch] = useState(value ?? '');
  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY) as string;
  const lastEmittedRef = useRef(value ?? '');

  useEffect(() => {
    const term =
      debouncedSearch.length >= MIN_SEARCH_LENGTH ? debouncedSearch : '';

    if (term === lastEmittedRef.current) return;

    lastEmittedRef.current = term;
    onChange(term);
  }, [debouncedSearch, onChange]);

  useEffect(() => {
    if (value === '' && search) {
      setSearch('');
    }
  }, [value]);

  const handleClear = () => {
    setSearch('');
    onChange('');
  };

  return (
    <InputGroup className={className}>
      <InputGroupAddon align="inline-start">
        <SearchIcon />
      </InputGroupAddon>
      <InputGroupInput
        placeholder={placeholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {search && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            variant="ghost"
            size="icon-xs"
            onClick={handleClear}
          >
            <XIcon />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
};
